import React from 'react';
import { 
  Box, Grid, Typography, Card, IconButton, Stack, 
  Button, useTheme, Paper, Divider, Chip, Table,
  TableBody, TableCell, TableHead, TableRow
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import HistoryIcon from '@mui/icons-material/History';
import InfoIcon from '@mui/icons-material/Info';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, PieChart, Pie, Cell, Legend, BarChart, Bar 
} from 'recharts';

// Data for the Distribution Bar Chart
const distributionData = [
  { year: '2021', distributed: 5000, called: 20000 },
  { year: '2022', distributed: 8000, called: 15000 },
  { year: '2023', distributed: 12000, called: 10000 },
  { year: '2024', distributed: 25000, called: 5000 },
  { year: '2025', distributed: 30000, called: 2000 },
];

const COLORS = ['#1976d2', '#00c49f', '#ffbb28'];

const InvestmentAnalytics = ({ investment, onBack }) => {
  if (!investment) return null;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f4f7f9', minHeight: '100vh' }}>
      
      {/* 1. Header Navigation */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <IconButton onClick={onBack} sx={{ bgcolor: 'white', border: '1px solid #ddd' }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight="900" sx={{ color: '#002e4c' }}>
              {investment.name}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Portfolio Management Detail • Last Updated: Feb 26, 2026
            </Typography>
          </Box>
        </Stack>
        <Button variant="contained" startIcon={<FileDownloadIcon />} sx={{ borderRadius: 2 }}>
          Download Report
        </Button>
      </Stack>

      <Grid container spacing={3}>
        {/* 2. Key Metrics row */}
        <Grid item xs={12} md={3}>
            <MetricCard title="TVPI" value={investment.tvpi} sub="Multiple of Money" color="#1976d2" />
        </Grid>
        <Grid item xs={12} md={3}>
            <MetricCard title="DPI" value={investment.dpi} sub="Realized Return" color="#2e7d32" />
        </Grid>
        <Grid item xs={12} md={3}>
            <MetricCard title="Called" value={investment.called} sub="Paid-in Capital" color="#ed6c02" />
        </Grid>
        <Grid item xs={12} md={3}>
            <MetricCard title="Uncalled" value={investment.uncalled} sub="Dry Powder" color="#757575" />
        </Grid>

        {/* 3. Top Row Charts */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 2, height: 400 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Portfolio Value Growth</Typography>
            <ResponsiveContainer width="100%" height="90%">
              <AreaChart data={[
                { month: 'Jan', value: 100000 }, { month: 'Mar', value: 120000 },
                { month: 'May', value: 115000 }, { month: 'Jul', value: 145000 },
                { month: 'Sep', value: 160000 }, { month: 'Nov', value: 185000 }
              ]}>
                <defs>
                  <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1976d2" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#1976d2" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" stroke="#888" fontSize={12} />
                <YAxis stroke="#888" fontSize={12} />
                <Tooltip />
                <Area type="monotone" dataKey="value" stroke="#1976d2" strokeWidth={3} fill="url(#colorValue)" />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 2, height: 400 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Asset Allocation</Typography>
            <ResponsiveContainer width="100%" height="90%">
              <PieChart>
                <Pie data={[{ name: 'Equity', value: 60 }, { name: 'Debt', value: 25 }, { name: 'Cash', value: 15 }]} 
                     innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                  {COLORS.map((color, i) => <Cell key={i} fill={color} />)}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="bottom" />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* 4. NEW ROW: Distribution Timeline vs Capital Called */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, borderRadius: 2, height: 400 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <HistoryIcon color="action" />
              <Typography variant="h6" fontWeight="bold">Annual Cash Flow</Typography>
            </Stack>
            <ResponsiveContainer width="100%" height="85%">
              <BarChart data={distributionData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="year" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="called" name="Capital Called" fill="#ed6c02" radius={[4, 4, 0, 0]} />
                <Bar dataKey="distributed" name="Distributions" fill="#2e7d32" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* 5. NEW ROW: Investment Summary Table */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, borderRadius: 2, height: 400, overflow: 'hidden' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <InfoIcon color="action" />
              <Typography variant="h6" fontWeight="bold">Investment Parameters</Typography>
            </Stack>
            <Table size="small">
              <TableBody>
                {[
                  { label: 'Vintage Year', value: '2021' },
                  { label: 'Legal Entity', value: 'Holt Xchange LP II' },
                  { label: 'Investment Strategy', value: 'Fintech Growth' },
                  { label: 'Management Fee', value: '2.0%' },
                  { label: 'Preferred Return', value: '8.0% Hurdle' },
                  { label: 'Reporting Currency', value: 'USD' },
                  { label: 'Next Capital Call', value: 'Expected Q3 2026' },
                ].map((row, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', border: 'none', py: 1.5 }}>{row.label}</TableCell>
                    <TableCell align="right" sx={{ border: 'none', py: 1.5 }}>{row.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Divider sx={{ my: 2 }} />
            <Box textAlign="center">
               <Typography variant="caption" color="textSecondary">Data verified by Compliance Office</Typography>
            </Box>
          </Paper>
        </Grid>

      </Grid>
    </Box>
  );
};

const MetricCard = ({ title, value, sub, color }) => (
  <Paper sx={{ p: 2, borderRadius: 2, borderLeft: `6px solid ${color}`, height: '100%' }}>
    <Typography variant="caption" color="textSecondary" fontWeight="bold" sx={{ textTransform: 'uppercase' }}>{title}</Typography>
    <Typography variant="h5" fontWeight="bold" sx={{ my: 0.5 }}>{value}</Typography>
    <Typography variant="caption" color="textSecondary">{sub}</Typography>
  </Paper>
);

export default InvestmentAnalytics;